import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * DetailsSidebar - A common side-panel for showing details
 * Used across the application for a consistent "drawer" experience
 */
const DetailsSidebar = ({ isOpen, onClose, title, children, icon: Icon, maxWidth = 'max-w-[500px]', footer }) => {
  
  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      {/* Backdrop with fade-in */}
      <div 
        className="absolute inset-0 bg-[#0F172A]/30 backdrop-blur-[3px] transition-opacity duration-300 animate-in fade-in" 
        onClick={onClose}
      />
      
      {/* Sidebar Content with slide-in */}
      <div className={`absolute right-0 top-0 h-full w-full ${maxWidth} bg-white dark:bg-[#262b33] shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 ease-out border-l border-[#E2E8F0] dark:border-gray-800`}>
        
        {/* Header - Fixed at top */}
        <div className="px-6 py-5 border-b border-[#E2E8F0] dark:border-gray-800 flex items-center justify-between bg-white/80 dark:bg-[#262b33]/80 backdrop-blur-md sticky top-0 z-20">
          <div className="flex items-center gap-3.5">
            {Icon && (
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#2563EB] shadow-sm border border-blue-100">
                <Icon className="w-5 h-5" />
              </div>
            )}
            <div>
              <h2 className="text-[18px] font-bold text-[#1E293B] dark:text-gray-200 leading-tight tracking-tight">{title}</h2>
              <p className="text-[11px] text-[#64748B] dark:text-gray-400 mt-0.5 font-bold uppercase tracking-widest flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                Information Overview
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-[#F1F5F9] dark:hover:bg-[#31373f] rounded-xl transition-all text-[#94A3B8] dark:text-gray-500 hover:text-[#475569] dark:hover:text-gray-400 group"
          >
            <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto bg-white dark:bg-[#262b33] p-6 custom-scrollbar">
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-150">
            {children}
          </div>
        </div>

        {/* Footer - Optional action area */}
        <div className="px-6 py-4 border-t border-[#E2E8F0] dark:border-gray-800 bg-[#F8FAFC] dark:bg-[#20242c] flex justify-end">
          {footer ?? (
            <button
              onClick={onClose}
              className="px-5 py-2 text-[13px] font-semibold text-[#64748B] dark:text-gray-400 hover:text-[#1E293B] dark:hover:text-gray-200 transition-colors"
            >
              Close Detail View
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailsSidebar;
